import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useVenueId } from '../../hooks/useUserData';
import { fetchAuditEvents } from '../../utils/auditService';
import AdminPageHeader from '../shared/AdminPageHeader';
import AdminSectionCard from '../shared/AdminSectionCard';
import AdminEmptyState from '../shared/AdminEmptyState';
import AdminNotice from '../shared/AdminNotice';
import AdminFilterBar from '../shared/AdminFilterBar';
import '../../styles/route_pages/Audit.css';

const ACTION_OPTIONS = [
  { value: '', label: 'Tümü' },
  { value: 'payment', label: 'Ödeme' },
  { value: 'discount', label: 'İndirim' },
  { value: 'void', label: 'İptal' },
  { value: 'close_check', label: 'Adisyon Kapat' },
  { value: 'open_shift', label: 'Vardiya Aç' },
  { value: 'close_shift', label: 'Vardiya Kapat' },
];

const ACTION_LABELS = {
  payment: 'Ödeme',
  discount: 'İndirim',
  void: 'İptal',
  close_check: 'Adisyon Kapat',
  open_shift: 'Vardiya Aç',
  close_shift: 'Vardiya Kapat',
};

const TARGET_LABELS = {
  check: 'Adisyon',
  order_item: 'Sipariş Kalemi',
  shift_session: 'Vardiya',
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const Audit = () => {
  const venueId = useVenueId();
  const { getAccessTokenSilently } = useAuth0();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [actionFilter, setActionFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');

  useEffect(() => {
    let active = true;

    if (!venueId) {
      setError('İşletme bağlamı bulunamadı.');
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const filters = {};
        if (actionFilter) filters.action = actionFilter;
        if (actorFilter.trim()) filters.actor = actorFilter.trim();
        if (fromFilter) filters.from = new Date(fromFilter).toISOString();
        if (toFilter) filters.to = new Date(toFilter + 'T23:59:59').toISOString();
        const data = await fetchAuditEvents(venueId, getAccessTokenSilently, filters);
        if (!active) return;
        setEvents(data || []);
      } catch (err) {
        if (!active) return;
        setError(err.message || 'Denetim kayıtları yüklenemedi.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => { active = false; };
  }, [venueId, getAccessTokenSilently, actionFilter, actorFilter, fromFilter, toFilter]);

  return (
    <div className="page-container audit-page">
      <AdminPageHeader
        title="Denetim Kaydı"
        subtitle="Hassas ticari işlemlerin değişmez kayıtları."
      />

      {error && (
        <AdminNotice tone="error" title="Yüklenemedi" message={error} />
      )}

      <div className="audit-filters">
        <AdminFilterBar
          options={ACTION_OPTIONS}
          value={actionFilter}
          onChange={setActionFilter}
          ariaLabel="İşlem türü filtresi"
        />
        <div className="audit-filter-group">
          <label htmlFor="audit-actor">Yapan</label>
          <input
            id="audit-actor"
            type="text"
            placeholder="Kullanıcı ID"
            value={actorFilter}
            onChange={(e) => setActorFilter(e.target.value)}
          />
        </div>
        <div className="audit-filter-group">
          <label htmlFor="audit-from">Başlangıç</label>
          <input
            id="audit-from"
            type="date"
            value={fromFilter}
            onChange={(e) => setFromFilter(e.target.value)}
          />
        </div>
        <div className="audit-filter-group">
          <label htmlFor="audit-to">Bitiş</label>
          <input
            id="audit-to"
            type="date"
            value={toFilter}
            onChange={(e) => setToFilter(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <AdminSectionCard title="Yükleniyor" subtitle="Denetim verileri hazırlanıyor." className="audit-state-card">
          <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
            Veriler hazırlanıyor...
          </div>
        </AdminSectionCard>
      ) : events.length === 0 ? (
        <AdminSectionCard title="Denetim Kayıtları" subtitle="Filtrelere uygun kayıt bulunamadı.">
          <AdminEmptyState
            title="Kayıt bulunamadı"
            description="Ticari işlemler gerçekleştikçe denetim kayıtları burada görünecektir."
          />
        </AdminSectionCard>
      ) : (
        <AdminSectionCard title="Denetim Kayıtları" subtitle={`Toplam ${events.length} kayıt.`}>
          <div className="audit-table-wrap">
            <table className="audit-table">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>İşlem</th>
                  <th>Hedef</th>
                  <th>Yapan</th>
                  <th>Sebep</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => (
                  <tr key={ev.id} onClick={() => setSelectedEvent(ev)}>
                    <td>{formatDateTime(ev.createdAt)}</td>
                    <td>
                      <span className={`audit-action-badge audit-action-badge--${ev.action}`}>
                        {ACTION_LABELS[ev.action] || ev.action}
                      </span>
                    </td>
                    <td>{TARGET_LABELS[ev.targetType] || ev.targetType} #{ev.targetId}</td>
                    <td>{ev.actorUserId}</td>
                    <td>{ev.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AdminSectionCard>
      )}

      {selectedEvent && (
        <div className="audit-detail-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="audit-detail-card" onClick={(e) => e.stopPropagation()}>
            <div className="audit-detail-header">
              <h3>Denetim Kaydı Detayı</h3>
              <span className={`audit-action-badge audit-action-badge--${selectedEvent.action}`}>
                {ACTION_LABELS[selectedEvent.action] || selectedEvent.action}
              </span>
            </div>

            <div className="audit-detail-body">
              <div className="audit-detail-section">
                <div className="audit-detail-section__title">İşlem Bilgisi</div>
                <div className="audit-detail-section__rows">
                  <div className="audit-detail-row">
                    <span className="audit-detail-row__label">İşlem</span>
                    <span className="audit-detail-row__value">{ACTION_LABELS[selectedEvent.action] || selectedEvent.action}</span>
                  </div>
                  <div className="audit-detail-row">
                    <span className="audit-detail-row__label">Hedef Türü</span>
                    <span className="audit-detail-row__value">{TARGET_LABELS[selectedEvent.targetType] || selectedEvent.targetType}</span>
                  </div>
                  <div className="audit-detail-row">
                    <span className="audit-detail-row__label">Hedef ID</span>
                    <span className="audit-detail-row__value">#{selectedEvent.targetId}</span>
                  </div>
                  <div className="audit-detail-row">
                    <span className="audit-detail-row__label">Tarih</span>
                    <span className="audit-detail-row__value">{formatDateTime(selectedEvent.createdAt)}</span>
                  </div>
                </div>
              </div>

              <div className="audit-detail-section">
                <div className="audit-detail-section__title">Yetkilendirme</div>
                <div className="audit-detail-section__rows">
                  <div className="audit-detail-row">
                    <span className="audit-detail-row__label">Yapan</span>
                    <span className="audit-detail-row__value">{selectedEvent.actorUserId}</span>
                  </div>
                  <div className="audit-detail-row">
                    <span className="audit-detail-row__label">Onaylayan</span>
                    <span className="audit-detail-row__value">{selectedEvent.overrideUserId || '—'}</span>
                  </div>
                  {selectedEvent.reason && (
                    <div className="audit-detail-row">
                      <span className="audit-detail-row__label">Sebep</span>
                      <span className="audit-detail-row__value">{selectedEvent.reason}</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedEvent.metadata && Object.keys(selectedEvent.metadata).length > 0 && (
                <div className="audit-detail-section">
                  <div className="audit-detail-section__title">Detaylar</div>
                  <div className="audit-detail-section__rows">
                    {Object.entries(selectedEvent.metadata).map(([key, val]) => (
                      <div className="audit-detail-row" key={key}>
                        <span className="audit-detail-row__label">{key}</span>
                        <span className="audit-detail-row__value">
                          {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="audit-detail-footer">
              <button
                type="button"
                className="audit-detail-close-btn"
                onClick={() => setSelectedEvent(null)}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Audit;
